import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { avatarColor, initials } from '../components/TournamentCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function timeLabel(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7)  return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/>
  </svg>
);

function Messages({ user }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API_URL}/api/messages/conversations`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setConversations(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, color: 'var(--text)' }}>

      {/* Header Spike */}
      <div style={{ padding: '16px 20px 12px', flexShrink: 0 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="display" style={{ fontSize: 26 }}>Messages</div>
        </div>

        <div className="row gap-2" style={{
          padding: '10px 14px', borderRadius: 'var(--r-md)',
          background: 'var(--ink-2)', border: '1px solid var(--line)',
        }}>
          <span style={{ color: 'var(--text-3)' }}><SearchIcon /></span>
          <input placeholder="Rechercher une conversation…" style={{
            flex: 1, background: 'transparent', border: 0, outline: 'none',
            color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
          }} />
        </div>
      </div>

      {/* Thread list */}
      <div className="screen-scroll" style={{ flex: 1 }}>
        {loading && (
          <div className="col gap-1" style={{ padding: '0 20px' }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div className="sk-badge" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="sk-line" style={{ width: '50%', height: 12, marginBottom: 6 }} />
                  <div className="sk-line" style={{ width: '70%', height: 10 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Aucune conversation</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6 }}>
              Ouvre le profil d'un joueur pour lui envoyer un message.
            </div>
          </div>
        )}

        <div style={{ padding: '0 20px' }}>
          {conversations.map(conv => {
            const badgeVal = conv.unread > 0 ? (conv.unread > 9 ? '9+' : conv.unread) : 0;

            if (conv.type === 'team') {
              if (!conv.lastMessage) return null;
              const displayName = conv.teamName || conv.tournamentName || 'Chat équipe';
              const teamBadge = (() => {
                const num = displayName.match(/\d+/)?.[0];
                if (num) return num;
                return displayName.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || '⬡';
              })();
              return (
                <button key={conv.id} onClick={() => navigate(`/messages/${conv.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--line)', padding: '12px 0', cursor: 'pointer', textAlign: 'left' }}>
                  {/* Col 1 — avatar */}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)', fontSize: teamBadge.length === 1 ? 18 : 13, fontWeight: 800, flexShrink: 0, fontFamily: 'JetBrains Mono, monospace' }}>
                    {teamBadge}
                  </div>
                  {/* Col 2 — nom + dernier message */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                      {displayName}
                    </span>
                    <span style={{ fontSize: 13, color: badgeVal ? 'var(--text)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                      {conv.lastMessage.text}
                    </span>
                  </div>
                  {/* Col 3 — heure + badge */}
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', color: badgeVal ? 'var(--sun)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>
                      {timeLabel(conv.updatedAt)}
                    </span>
                    {badgeVal > 0 && (
                      <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: 'var(--sun)', color: 'var(--ink)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>
                        {badgeVal}
                      </span>
                    )}
                  </div>
                </button>
              );
            }
            const other = conv.otherUser;
            if (!other || !other.firstName) return null;
            const unread = conv.unread || 0;
            const badge = unread > 9 ? '9+' : unread;
            return (
              <button key={conv.id} onClick={() => navigate(`/messages/${conv.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--line)', padding: '12px 0', cursor: 'pointer', textAlign: 'left' }}>
                {/* Col 1 — avatar */}
                <div className={`av-circle ${other.avatarUrl ? '' : avatarColor(other.id)}`}
                  style={{ width: 44, height: 44, flexShrink: 0, fontSize: 14, fontWeight: 700 }}>
                  {other.avatarUrl
                    ? <img src={other.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : initials(other.firstName, other.lastName)
                  }
                </div>
                {/* Col 2 — nom + dernier message */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, fontWeight: unread ? 700 : 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {other.firstName} {other.lastName}
                  </span>
                  <span style={{ fontSize: 13, color: badge ? 'var(--text)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {conv.lastMessage
                      ? (conv.lastMessage.senderId === user?.id ? 'Vous : ' : '') + conv.lastMessage.text
                      : 'Nouvelle conversation'}
                  </span>
                </div>
                {/* Col 3 — heure + badge */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', color: badge ? 'var(--sun)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    {timeLabel(conv.updatedAt)}
                  </span>
                  {badge > 0 && (
                    <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: 'var(--sun)', color: 'var(--ink)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>
                      {badge}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ height: 28 }} />
      </div>
    </div>
  );
}

export default Messages;
